import { reviewDocumentFields, reviewDocumentAdjustment, draftClientCellDecision } from 'src/content/applications/quote/documentRevisionReview';
import { clientRequestDecisionColor } from 'src/content/applications/quote/updateQuote/ComparisonTableCell';
import { hasPendingClientRate } from 'src/content/applications/quote/clientRequestPricing';
import QuoteTimeInput from 'src/content/applications/quote/QuoteTimeInput';
import { formatAttendanceDisplayTime, parseAttendanceDisplayTime } from 'src/utils/attendanceHourCycle';
import { rateTypeDisplayLabel } from 'src/utils/rateTypeIdentity';
import BoundClientRateSelect, { unresolvedClientRate } from 'src/content/applications/quote/BoundClientRateSelect';
import { SavedQuoteRateTypeOption } from 'src/content/applications/quote/updateQuote/savedQuoteRateTypes';
import { visuallyHidden } from '@mui/utils';
import {DocumentExtraCell} from 'src/content/applications/quote/clientDocumentOptions';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Autocomplete,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  CircularProgress,
  Box
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';
import { formatDate } from 'src/utils/date';
import { formatNumber } from 'src/utils/formatters';
import {
  actualMoveJob,
  deleteActualShift,
  getShiftUsers
} from 'src/helper/backend_helper';
import ShiftUsersDrawer from './ShiftUsersDrawer';
import ShiftExtrasModal from './ShiftExtrasModal';
import ActualsComparisonTableCell from './ActualsComparisonTableCell';
import { calculateShiftTotal } from '../../../../../../quote/quoteFeeColumns';

const ShiftValidationSchema = Yup.object({
  startDate: Yup.date().required('Start Date is required'),
  endDate: Yup.date().required('End Date is required'),
  position: Yup.string().required('Position is required'),
  quantity: Yup.number()
    .integer('Quantity must be a whole number')
    .max(2147483647, 'Quantity is too large')
    .positive('Quantity must be positive')
    .required('Quantity is required'),
  startTime: Yup.string().required('Start Time is required'),
  endTime: Yup.string().required('End Time is required'),
  notes: Yup.string(),
  rateType: Yup.string(),
  callType: Yup.string().required('Call Type is required'),
  mealBreak: Yup.string().required('Meal Break is required'),
  rate: Yup.number()
    .min(0, 'Client Rate must be 0 or greater')
    .required('Client Rate is required'),
  desiredRate: Yup.number()
    .min(0, 'Desired Rate must be 0 or greater')
    .required('Desired Rate is required'),
  registerHours: Yup.number()
    .min(0, 'ST Hours must be 0 or greater')
    .required('ST Hours is required'),
  overTimeHours: Yup.number()
    .min(0, 'OT Hours must be 0 or greater')
    .required('OT Hours is required'),
  DoubleTimeHours: Yup.number()
    .min(0, 'DT Hours must be 0 or greater')
    .required('DT Hours is required'),
  addOns: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .optional(),
  miscFees: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .optional()
});

const cellBorder = '3px solid #E8BF21';
const cellSx = {
  border: cellBorder,
  fontSize: '13px',
  color: '#000',
  padding: '4px 6px',
  whiteSpace: 'nowrap'
};

const editCellSx = {
  ...cellSx,
  backgroundColor: '#fffef5',
  padding: '2px 3px',
  '& .MuiInputBase-root': {
    fontSize: '13px',
    '&:before, &:after': {
      display: 'none'
    }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '& .MuiAutocomplete-root .MuiOutlinedInput-root': {
    padding: '0px'
  }
};

const borderlessInputSx = {
  '& .MuiInputBase-input': {
    padding: '4px 0px',
    fontSize: '13px'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  }
};

const numberInputSx = {
  ...borderlessInputSx,
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    {
      WebkitAppearance: 'none',
      margin: 0
    }
};

const calculateHours = (startTime: string, endTime: string) => {
  if (!startTime || !endTime) return 0;
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  let hours = endHour - startHour;
  if (hours < 0) hours += 24;
  const total = (hours + (endMinute - startMinute) / 60).toFixed(2);
  return Number(total);
};

const getEndDate = (startDate: string, startTime: string, endTime: string) => {
  if (!startDate || !startTime || !endTime) return startDate;
  const start = moment(startTime, 'HH:mm');
  const end = moment(endTime, 'HH:mm');
  if (end.isBefore(start)) {
    return moment(startDate).add(1, 'day').format('YYYY-MM-DD');
  }
  return startDate;
};

interface ActualsInlineShiftRowProps {
  rateTypeOptions?: SavedQuoteRateTypeOption[];
  documentSettings?: any;
  documentOnly?: boolean;
  onDraftChange?: (row: any) => void;
  restoredDraft?: any;
  position: any;
  isEditing: boolean;
  onStartEdit: () => void;
  onSave: (values: any) => void;
  onCancel: () => void;
  onDuplicate: (values: any) => void;
  onDelete: (id: any) => void;
  onAcceptHistoryShift?: (shift: any) => void;
  positionsArray: any;
  allShifts: any[];
  comparisonData?: any;
  includePerDiem?: boolean;
  includeMiscFees?: boolean;
}

const ActualsInlineShiftRow: React.FC<ActualsInlineShiftRowProps> = ({
  rateTypeOptions = [],
  documentSettings,
  position,
  documentOnly = false,
  onDraftChange,
  restoredDraft,
  isEditing,
  onStartEdit,
  onSave,
  onCancel,
  onDelete,
  onDuplicate,
  onAcceptHistoryShift,
  positionsArray,
  allShifts,
  comparisonData,
  includePerDiem = true,
  includeMiscFees = false
}) => {
  const [positionInput, setPositionInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const rowRef = useRef<HTMLTableRowElement>(null);
  const isSavingRef = useRef(false);

  // Track which comparison fields have had their icons hidden
  const [resolvedFields, setResolvedFields] = useState<Set<string>>(new Set());
  const [dateIconsHidden, setDateIconsHidden] = useState(false);
  const [dateTooltipOpen, setDateTooltipOpen] = useState(false);

  const handleResolveField = (field: string) => {
    setResolvedFields((prev) => {
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  };

  // Actuals-specific state for quantity management
  const [shiftUsersModalOpen, setShiftUsersModalOpen] = useState(false);
  const [shiftUsers, setShiftUsers] = useState([]);
  const [usersToRemoveQuantity, setUsersToRemoveQuantity] = useState(0);
  const [removedJobs, setRemovedJobs] = useState([]);

  // Extras modal state (for quantity increase)
  const [extrasModalOpen, setExtrasModalOpen] = useState(false);
  const [shiftExtras, setShiftExtras] = useState([]);
  const [quantityToAdd, setQuantityToAdd] = useState(0);
  const [addedExtras, setAddedExtras] = useState([]);

  const allPositions = useMemo(() => {
    return (
      positionsArray?.data?.data?.flatMap((category) =>
        category.positions.map((pos) => ({
          ...pos,
          category: (category.name || '').trim().toUpperCase()
        }))
      ) || []
    );
  }, [positionsArray]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      shiftNumber: position?.shiftNumber || '',
      shiftId: position?.shiftId,
      startDate: position?.startDate
        ? position.startDate.includes('T')
          ? position.startDate.split('T')[0]
          : position.startDate
        : '',
      endDate: position?.endDate
        ? position.endDate.includes('T')
          ? position.endDate.split('T')[0]
          : position.endDate
        : '',
      position: position?.position || '',
      quantity: position?.quantity || 1,
      extraQuantity: position?.extraQuantity || 0,
      availableQuantity: position?.availableQuantity || 0,
      startTime: position?.startTime || '08:00',
      endTime: position?.endTime || '19:00',
      notes: position?.notes || '',
      rateType: position?.rateType || '',
      callType: position?.callType || 'Show Day',
      mealBreak: position?.mealBreak || '1hr Walk',
      rate: position?.clientRatePending ? null : position?.rate ?? 0,
      desiredRate: position?.desiredRate || 0,
      calculatedHours: position?.calculatedHours || 10,
      registerHours: position?.registerHours || 0,
      overTimeHours: position?.overTimeHours || 0,
      DoubleTimeHours: position?.DoubleTimeHours || 0,
      addOns: position?.addOns || 0,
      miscFees: position?.miscFees || 0,
      totals: position?.clientRatePending ? null : position?.totals ?? 0,
      clientRatePending: Boolean(position?.clientRatePending),
      clientRequestDraftDecisions: position?.clientRequestDraftDecisions || [],
      ...restoredDraft
    },
    validationSchema: ShiftValidationSchema,
    onSubmit: async (values) => {
      if (documentOnly) { onSave(reviewDocumentAdjustment(position, { ...values, id: position.id, clientRatePending: hasPendingClientRate(values) }, comparisonData)); return; }
      const startDate = dayjs(values.startDate);
      const endDate = dayjs(values.endDate);
      if (startDate.isValid() && endDate.isValid()) {
        const diffInDays = endDate.diff(startDate, 'day');
        if (diffInDays !== 0 && diffInDays !== 1) {
          toast.error('Date must be the same day or overnight');
          return;
        }
      }
      // Handle live-shift quantity changes
      // if (position.isLive) {
      //   const jobs = await getShiftUsers(position.shiftId);

      //   // QUANTITY DECREASE — select users to send to extras
      //   if (position?.quantity > values?.quantity) {
      //     const toRemove = position?.quantity - values?.quantity;
      //     if (
      //       toRemove !== removedJobs.length &&
      //       (removedJobs.length === 0 || toRemove > removedJobs.length)
      //     ) {
      //       setShiftUsersModalOpen(true);
      //       const filteredUsers = (jobs?.data?.data || []).filter(
      //         (u: any) => u?.NCNS !== true && u?.extra !== true
      //       );
      //       setShiftUsers(filteredUsers);
      //       setUsersToRemoveQuantity(toRemove);
      //       return;
      //     }
      //   }

        // QUANTITY INCREASE — check if extras exist to pull back in
        // if (values?.quantity > position?.quantity) {
        //   const allUsers = jobs?.data?.data || [];
        //   const extras = allUsers.filter(
        //     (j: any) => j?.extra === true && j?.NCNS !== true
        //   );
        //   const toAdd = values?.quantity - position?.quantity;
        //   if (extras.length > 0 && addedExtras.length === 0) {
        //     setShiftExtras(extras);
        //     setQuantityToAdd(toAdd);
        //     setExtrasModalOpen(true);
        //     return;
        //   }
        // }
      // }

      // For non-live shifts (new shifts), just save locally — no API calls
      if (!position.isLive) {
        onSave({
          ...values,
          id: position.id,
          shiftId: values.shiftId
        });
        return;
      }

      try {
        setLoading(true);

        // Move users to extras (quantity decrease)
        // if (removedJobs.length > 0) {
        //   await actualMoveJob({
        //     shiftId: values.shiftId,
        //     jobIds: removedJobs,
        //     type: 'moveToExtra'
        //   });
        // }

        // Move users from extras back to shift (quantity increase)
        // if (addedExtras.length > 0 || values?.quantity > position?.quantity) {
        //   const totalIncrease = values.quantity - position.quantity;
        //   const exceededQuantity =
        //     totalIncrease > addedExtras.length
        //       ? totalIncrease - addedExtras.length
        //       : 0;

        //   await actualMoveJob({
        //     shiftId: values.shiftId,
        //     jobIds: addedExtras,
        //     type: 'moveFromExtra',
        //     exceededQuantity
        //   });
        // }

        onSave({
          ...values,
          id: position.id,
          shiftId: values.shiftId,
          removedJobs,
          addedExtras
        });
        setRemovedJobs([]);
        setAddedExtras([]);
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to update shift. Please try again.';
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  });

  // Reset state when editing starts
  useEffect(() => {
    if (isEditing) {
      setRemovedJobs([]);
      setAddedExtras([]);
    }
  }, [isEditing, position?.id]);

  useEffect(() => {
    if (isEditing && documentOnly) onDraftChange?.({ ...position, ...formik.values, id: position.id });
  }, [isEditing, documentOnly, formik.values]);
  const reviewFields = (fields: string[], decision: 'accept' | 'reject') => {
    if (!position.quoteReferenceLabel) onSave(reviewDocumentFields(position, comparisonData, fields, decision));
  };
  const reviewField = (field: string, decision: 'accept' | 'reject') => reviewFields(field === 'startTime' ? ['startTime', 'endTime'] : [field], decision);

  const saveCurrentRef = React.useRef<() => Promise<any>>();

  // Auto-save on click outside the editing row
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (isSavingRef.current) return;

      const target = event.target as HTMLElement;
      if (
        (rowRef.current && rowRef.current.contains(event.target as Node)) ||
        target.closest('.MuiPopover-root') ||
        target.closest('.MuiAutocomplete-popper') ||
        target.closest('.MuiModal-root') ||
        target.closest('.MuiMenu-root') ||
        target.closest('.MuiTooltip-popper') ||
        target.closest('.MuiDrawer-root') ||
        target.closest('.swal2-container') ||
        target.closest('[role="listbox"]') ||
        target.closest('[role="presentation"]')
      )
        return;

      isSavingRef.current = true;
      setIsSaving(true);
      saveCurrentRef.current?.().finally(() => {
        isSavingRef.current = false;
        setIsSaving(false);
      });
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing]);

  // Calculate hours and totals
  useEffect(() => {
    if (!isEditing) return;

    let calculatedHours = calculateHours(
      formik.values.startTime,
      formik.values.endTime
    );
    if (formik.values.mealBreak === '1hr Walk') {
      calculatedHours -= 1;
    }
    formik.setFieldValue('calculatedHours', calculatedHours);

    const endDate = getEndDate(
      formik.values.startDate,
      formik.values.startTime,
      formik.values.endTime
    );
    if (endDate !== formik.values.endDate) {
      formik.setFieldValue('endDate', endDate);
    }

    formik.setFieldValue(
      'totals',
      hasPendingClientRate(formik.values) ? null : calculateShiftTotal(formik.values, includePerDiem, includeMiscFees)
    );

    // Available quantity tracking
    const available = formik.values.quantity - (position?.quantity || 0);
    const availableQuantity = (position?.availableQuantity || 0) + available;
    formik.setFieldValue('availableQuantity', availableQuantity);
  }, [
    formik.values.startTime,
    formik.values.endTime,
    formik.values.rate,
    formik.values.registerHours,
    formik.values.overTimeHours,
    formik.values.DoubleTimeHours,
    formik.values.addOns,
    formik.values.miscFees,
    formik.values.quantity,
    formik.values.mealBreak,
    formik.values.startDate,
    isEditing,
    position?.quantity,
    position?.availableQuantity,
    includePerDiem,
    includeMiscFees
  ]);

  const handleDeleteShift = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: '',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      didOpen: () => {
        (Swal.getContainer() as HTMLElement).style.zIndex = '3000';
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsDeleting(true);
        try {
          if (position.isLive && !documentOnly) {
            await deleteActualShift(position.id);
          }
          onDelete(position.id);
        } catch (error) {
          toast.error(
            error?.response?.data?.message || 'Error deleting shift'
          );
        } finally {
          setIsDeleting(false);
        }
      }
    });
  };

  const handleSaveClick = async (requestedValues?: typeof formik.values) => {
    const values = requestedValues || formik.values;
    if (documentOnly) { onSave(reviewDocumentAdjustment(position, { ...formik.values, id: position.id, clientRatePending: hasPendingClientRate(formik.values) }, comparisonData)); return true; }
    const errors = await formik.validateForm(values);
    if (Object.keys(errors).length > 0) {
      formik.setTouched(
        Object.keys(values).reduce((touched, key) => {
          touched[key] = true;
          return touched;
        }, {})
      );

      const fieldLabels = {
        startDate: 'Start Date',
        position: 'Position',
        quantity: 'Quantity',
        startTime: 'Start Time',
        endTime: 'End Time',
        rateType: 'Rate Type',
        callType: 'Call Type',
        mealBreak: 'Meal Break',
        rate: 'Client Rate',
        desiredRate: 'Desired Rate',
        registerHours: 'ST Hours',
        overTimeHours: 'OT Hours',
        DoubleTimeHours: 'DT Hours'
      };

      const missingFields = Object.keys(errors)
        .map((key) => fieldLabels[key] || key)
        .join(', ');

      toast.error(`Please fill all required fields: ${missingFields}`);
      return false;
    }

    const startDate = dayjs(values.startDate);
    const endDate = dayjs(values.endDate);
    if (startDate.isValid() && endDate.isValid()) {
      const diffInDays = endDate.diff(startDate, 'day');
      if (diffInDays !== 0 && diffInDays !== 1) {
        toast.error('Date must be the same day or overnight');
        return false;
      }
    }

    if (requestedValues) {
      const hours = calculateHours(values.startTime, values.endTime) - (values.mealBreak === '1hr Walk' ? 1 : 0);
      onSave({ ...values, id: position.id, shiftId: values.shiftId,
        calculatedHours: hours,
        availableQuantity: (position.availableQuantity || 0) + values.quantity - position.quantity,
        removedJobs, addedExtras });
      return true;
    }
    await formik.handleSubmit();
    return true;
  };

  saveCurrentRef.current = handleSaveClick;

  const handleNumericChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (val === '') {
        formik.setFieldValue(field, 0);
        return;
      }
      if (['rate', 'desiredRate', 'addOns', 'miscFees'].includes(field)) {
        const regex = /^\d*\.?\d{0,2}$/;
        if (!regex.test(val)) return;
      }
      formik.setFieldValue(field, Number(val));
    };

  // ──── EDIT MODE ────
  if (isEditing) {
    return (
      <>
        <TableRow
          ref={rowRef}
          sx={{
            backgroundColor: '#fffef5',
            position: 'relative',
            pointerEvents: isSaving ? 'none' : 'auto'
          }}
        >


          <TableCell align="center" sx={{ ...editCellSx, minWidth: 160 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {isSaving && (
                <CircularProgress size={14} sx={{ color: '#665201ff' }} />
              )}
              <TextField
                size="small"
                type="date"
                name="startDate"
                value={formik.values.startDate}
                onChange={formik.handleChange}
                sx={{ width: 140, ...borderlessInputSx }}
                InputProps={{ sx: { fontSize: '14px' } }}
              />
            </Stack>
          </TableCell>

          <TableCell align="center" sx={editCellSx}>
            <TextField
              size="small"
              type="number"
              name="quantity"
              value={formik.values.quantity}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputProps={{ min: 1, step: 1, max: 2147483647, 'aria-label': 'Quantity' }}
              FormHelperTextProps={{ sx: { whiteSpace: 'normal', overflowWrap: 'normal', wordBreak: 'normal', fontSize: '11px', lineHeight: 1.25, mx: 0 } }}
              error={Boolean((formik.touched.quantity || formik.submitCount) && formik.errors.quantity)}
              helperText={(formik.touched.quantity || formik.submitCount > 0) && formik.errors.quantity ? <><span aria-hidden="true">Whole number &gt; 0</span><Box component="span" sx={visuallyHidden}>{String(formik.errors.quantity)}</Box></> : undefined}
              sx={{ width: 55, ...numberInputSx }}
              InputProps={{ sx: { fontSize: '14px' } }}
            />
          </TableCell>{documentSettings?.includeArea && <DocumentExtraCell position={position} field="area"/>}

          <TableCell align="center" sx={editCellSx}>
            <Autocomplete
              size="small"
              options={allPositions}
              groupBy={(option) => option.category}
              getOptionLabel={(option) => option.name || ''}
              value={
                allPositions.find(
                  (p) => p.name === formik.values.position
                ) || null
              }
              onChange={(_, newValue) =>
                formik.setFieldValue(
                  'position',
                  newValue ? newValue.name : ''
                )
              }
              inputValue={positionInput}
              onInputChange={(_, val) => setPositionInput(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Position"
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    sx: { fontSize: '14px' }
                  }}
                />
              )}
              sx={{ minWidth: 180 }}
            />
          </TableCell>{documentSettings?.includeToolsGear && <DocumentExtraCell position={position} field="toolsGear"/>}

          <TableCell align="center" sx={editCellSx}>
            <Stack direction="column" spacing={0.5} sx={{ minWidth: 0, '& .MuiTextField-root': { width: '100%', maxWidth: '100%' } }}>
              <QuoteTimeInput label="Start Time" name="startTime"
value={formik.values.startTime}
                onChange={formik.handleChange} formatDisplay={clock => formatAttendanceDisplayTime(clock, '12')}
                parseDisplay={draft => parseAttendanceDisplayTime(draft, '12')} />

              <QuoteTimeInput label="End Time" name="endTime"
value={formik.values.endTime}
                onChange={formik.handleChange} formatDisplay={clock => formatAttendanceDisplayTime(clock, '12')}
                parseDisplay={draft => parseAttendanceDisplayTime(draft, '12')} />
            </Stack>
          </TableCell>

          <TableCell align="center" sx={editCellSx}>
            <TextField
              size="small"
              name="notes"
              value={formik.values.notes}
              onChange={formik.handleChange}
              sx={{ minWidth: 100, ...borderlessInputSx }}
              InputProps={{ sx: { fontSize: '14px' } }}
            />
          </TableCell>

          <TableCell align="center" sx={editCellSx}>
            <BoundClientRateSelect value={formik.values.rateType} onChange={formik.handleChange} options={rateTypeOptions} />
          </TableCell>

          <TableCell align="center" sx={editCellSx}>
            <FormControl size="small" fullWidth>
              <Select
                variant="standard"
                name="callType"
                value={formik.values.callType}
                onChange={formik.handleChange}
                disableUnderline
                sx={{ fontSize: '14px' }}
              >
                <MenuItem value="Setup">Setup</MenuItem>
                <MenuItem value="Strike">Strike</MenuItem>
                <MenuItem value="Show Day">Show Day</MenuItem>
                <MenuItem value="Set & Show">Set & Show</MenuItem>
                <MenuItem value="Show & Strike">Show & Strike</MenuItem>
                <MenuItem value="Rehearsal">Rehearsal</MenuItem>
                <MenuItem value="Set/Show/Strike">Set/Show/Strike</MenuItem>
              </Select>
            </FormControl>
          </TableCell>

          <TableCell align="center" sx={editCellSx}>
            <FormControl size="small" fullWidth>
              <Select
                variant="standard"
                name="mealBreak"
                value={formik.values.mealBreak}
                onChange={formik.handleChange}
                disableUnderline
                sx={{ fontSize: '14px' }}
              >
                <MenuItem value="1hr Walk">1hr Walk</MenuItem>
                <MenuItem value="2hr Walk">2hr walk</MenuItem>
                <MenuItem value="TBD">TBD</MenuItem>
                <MenuItem value="30 min">30 min</MenuItem>
                <MenuItem value="Skipped">Skipped</MenuItem>
                <MenuItem value="MP">MP</MenuItem>
                <MenuItem value="N/A">N/A</MenuItem>
              </Select>
            </FormControl>
          </TableCell>

          <TableCell align="center" sx={editCellSx}>
            <TextField
              size="small"
              type="number"
              name="rate"
              value={formik.values.rate === 0 ? '' : formik.values.rate}
              onChange={handleNumericChange('rate')}
              sx={{ width: 80, ...numberInputSx }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <span style={{ fontSize: '14px', color: '#000' }}>$</span>
                  </InputAdornment>
                ),
                sx: { fontSize: '14px' }
              }}
              inputProps={{ step: '0.01' }}
            />
          </TableCell>

          <TableCell align="center" sx={editCellSx}>
            <TextField
              size="small"
              type="number"
              name="registerHours"
              value={
                formik.values.registerHours === 0
                  ? ''
                  : formik.values.registerHours
              }
              onChange={handleNumericChange('registerHours')}
              sx={{ width: 60, ...numberInputSx }}
              InputProps={{ sx: { fontSize: '14px' } }}
            />
          </TableCell>

          <TableCell align="center" sx={editCellSx}>
            <TextField
              size="small"
              type="number"
              name="overTimeHours"
              value={
                formik.values.overTimeHours === 0
                  ? ''
                  : formik.values.overTimeHours
              }
              onChange={handleNumericChange('overTimeHours')}
              sx={{ width: 60, ...numberInputSx }}
              InputProps={{ sx: { fontSize: '14px' } }}
            />
          </TableCell>

          <TableCell align="center" sx={editCellSx}>
            <TextField
              size="small"
              type="number"
              name="DoubleTimeHours"
              value={
                formik.values.DoubleTimeHours === 0
                  ? ''
                  : formik.values.DoubleTimeHours
              }
              onChange={handleNumericChange('DoubleTimeHours')}
              sx={{ width: 60, ...numberInputSx }}
              InputProps={{ sx: { fontSize: '14px' } }}
            />
          </TableCell>

          {(documentSettings?.includePerDiem ?? includePerDiem) && (
            <TableCell align="center" sx={editCellSx}>
              <TextField
                size="small"
                type="number"
                name="addOns"
                value={formik.values.addOns === 0 ? '' : formik.values.addOns}
                onChange={handleNumericChange('addOns')}
                sx={{ width: 100, ...numberInputSx }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span style={{ fontSize: '14px', color: '#000' }}>$</span>
                    </InputAdornment>
                  ),
                  sx: { fontSize: '14px' }
                }}
                inputProps={{ step: '0.01' }}
              />
            </TableCell>
          )}

          {(documentSettings?.includeMiscFees ?? includeMiscFees) && (
            <TableCell align="center" sx={editCellSx}>
              <TextField
                size="small"
                type="number"
                name="miscFees"
                value={
                  formik.values.miscFees === 0 ? '' : formik.values.miscFees
                }
                onChange={handleNumericChange('miscFees')}
                sx={{ width: 100, ...numberInputSx }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span style={{ fontSize: '14px', color: '#000' }}>$</span>
                    </InputAdornment>
                  ),
                  sx: { fontSize: '14px' }
                }}
                inputProps={{ step: '0.01' }}
              />
            </TableCell>
          )}

          <TableCell align="center" sx={editCellSx}>
            ${formatNumber(formik.values.totals)}
          </TableCell>
        </TableRow>

        {/* Actuals-specific modals */}
        <ShiftUsersDrawer
          open={shiftUsersModalOpen}
          onClose={() => setShiftUsersModalOpen(false)}
          usersData={shiftUsers}
          usersToRemoveQuantity={usersToRemoveQuantity}
          onSave={(selected) => {
            setRemovedJobs(selected);
            setShiftUsersModalOpen(false);
            // Re-trigger submit now that removedJobs are set
            setTimeout(() => formik.handleSubmit(), 100);
          }}
        />

        <ShiftExtrasModal
          open={extrasModalOpen}
          handleClose={() => setExtrasModalOpen(false)}
          extras={shiftExtras}
          quantityToAdd={quantityToAdd}
          onSave={(selectedExtraIds) => {
            setAddedExtras(selectedExtraIds);
            setExtrasModalOpen(false);
            // Re-trigger submit now that addedExtras are set
            setTimeout(() => formik.handleSubmit(), 100);
          }}
        />
      </>
    );
  }

  // ──── DISPLAY MODE ────
  const status = comparisonData?.status || 'unchanged';
  const isDeleted = Boolean(position.quoteReferenceLabel) || status === 'deleted';
  const isNew = status === 'new';
  const isModified = status === 'modified';

  const dateDecision = draftClientCellDecision(position, comparisonData, ['startDate', 'endDate']);
  const hasDateChanged = !!(comparisonData?.changes?.startDate || comparisonData?.changes?.endDate);

  const formatDateForDisplay = (dateValue: any) => {
    if (!dateValue) return 'N/A';
    if (typeof dateValue === 'string' && dateValue.includes('T')) {
      return dateValue.split('T')[0];
    }
    return dateValue;
  };

  const baseStartDate = comparisonData?.changes?.startDate?.base ? formatDateForDisplay(comparisonData.changes.startDate.base) : null;
  const reqStartDate = comparisonData?.changes?.startDate?.requested ? formatDateForDisplay(comparisonData.changes.startDate.requested) : null;
  const baseEndDate = comparisonData?.changes?.endDate?.base ? formatDateForDisplay(comparisonData.changes.endDate.base) : null;
  const reqEndDate = comparisonData?.changes?.endDate?.requested ? formatDateForDisplay(comparisonData.changes.endDate.requested) : null;

  // Handler to accept new values from comparison tooltip
  const handleAcceptChange = async (field: string, newValue: any) => {
    let cleanValue = newValue;
    if (typeof newValue === 'string' && newValue.startsWith('$')) {
      cleanValue = parseFloat(newValue.replace('$', '').replace(/,/g, '')) || 0;
    }
    const values = { ...formik.values, [field]: cleanValue };
    if (field === 'startTime' && typeof newValue === 'string' && newValue.includes('-')) {
      const [startTime, endTime] = newValue.split('-');
      values.startTime = startTime.trim();
      values.endTime = endTime.trim();
      values.endDate = getEndDate(values.startDate, values.startTime, values.endTime);
    }
    // Commit the explicitly accepted draft, independent of edit-mode/Formik timing.
    if (await handleSaveClick(values)) handleResolveField(field);
  };

  const dateDisplay = formatDate(position?.startDate || comparisonData?.historyData?.startDate);
  const dateStr = moment(position?.startDate || comparisonData?.historyData?.startDate).format('ddd').toUpperCase();

  return (
    <TableRow data-document-revision={isDeleted || isNew ? "revision" : undefined}
      sx={{
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#fafaf0' },
        backgroundColor: isNew ? '#e8f5e9' : isDeleted ? '#ffebee' : 'inherit',
        opacity: isDeleted ? 0.7 : 1
      }}
    >


      {/* Date Column */}
      <TableCell
        align="center"
        sx={{
          ...cellSx,
          cursor: 'pointer',
          fontWeight: 'bold',
          textDecoration: isDeleted ? 'line-through' : 'none',
          backgroundColor: isNew ? '#c8e6c9' : isDeleted ? '#ffcdd2' : hasDateChanged ? clientRequestDecisionColor(dateDecision) : undefined
        }}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title="Duplicate" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (!position.quoteReferenceLabel) onDuplicate(position);
              }}
              sx={{ color: '#555', p: 0 }}
            >
              <ContentCopyIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (!position.quoteReferenceLabel) handleDeleteShift();
              }}
              sx={{ color: 'red', p: 0 }}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DeleteTwoToneIcon sx={{ fontSize: '18px' }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip
            placement='top'
            open={dateTooltipOpen}
            onOpen={() => setDateTooltipOpen(true)}
            onClose={() => setDateTooltipOpen(false)}
            title={
              hasDateChanged ? (
                <Stack spacing={1} sx={{ color: 'white' }}>
                  <Box>
                    <strong>New:</strong> {reqStartDate || reqEndDate}
                  </Box>
                  <Box>
                    <strong>Original:</strong> {baseStartDate || baseEndDate}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, pt: 0.5 }}>
                    {!dateIconsHidden && (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (documentOnly) { reviewFields(['startDate', 'endDate'], 'accept'); return; }
                            void handleSaveClick({ ...formik.values,
                              startDate: reqStartDate || formik.values.startDate,
                              endDate: reqEndDate || formik.values.endDate
                            }).then(saved => { if (saved) setDateIconsHidden(true); });
                          }}
                          sx={{
                            color: 'white',
                            backgroundColor: 'rgba(76, 175, 80, 0.5)',
                            '&:hover': {
                              backgroundColor: 'rgba(76, 175, 80, 0.7)'
                            },
                            p: 0.5
                          }}
                          title="Accept New Date"
                        >
                          <CheckIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (documentOnly) { reviewFields(['startDate', 'endDate'], 'reject'); return; }
                            setDateIconsHidden(true);
                          }}
                          sx={{
                            color: 'white',
                            backgroundColor: 'rgba(244, 67, 54, 0.5)',
                            '&:hover': {
                              backgroundColor: 'rgba(244, 67, 54, 0.7)'
                            },
                            p: 0.5
                          }}
                          title="Reject Change"
                        >
                          <CloseIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Stack>
              ) : isDeleted ? (
                'Deleted Shift'
              ) : (
                'Click to Edit'
              )
            }
            arrow
          >
            <span style={{ textDecoration: isDeleted ? 'line-through' : 'underline', marginLeft: '4px' }}>
              {dateStr} {dateDisplay}
              {isDeleted && <Box component="span" sx={{ display: 'block', color: 'error.main', whiteSpace: 'normal' }}>{position.quoteReferenceLabel || 'Deleted'}</Box>}
              {isNew && (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <span style={{ marginLeft: '4px', color: '#2e7d32', fontWeight: 'bold' }}>[NEW]</span>
                  {onAcceptHistoryShift && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAcceptHistoryShift(position);
                      }}
                      sx={{ ml: 1, p: 0.5, color: '#2e7d32' }}
                      title="Add This Shift Back to Actuals"
                    >
                      <CheckIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                  )}
                </Box>
              )}
            </span>
          </Tooltip>
        </Stack>
      </TableCell>

      {/* Quantity */}
      <ActualsComparisonTableCell
        value={position?.quantity || comparisonData?.historyData?.quantity}
        field="quantity"
        decision={draftClientCellDecision(position, comparisonData, ['quantity'])}
        originalValue={comparisonData?.changes?.quantity?.base}
        onDecision={documentOnly ? decision => reviewField('quantity', decision) : undefined}
        requestedValue={comparisonData?.status !== 'deleted' ? comparisonData?.changes?.quantity?.requested : undefined}
        hasChanged={!!comparisonData?.changes?.quantity}
        isDeleted={isDeleted}
        cellSx={cellSx}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
        iconsHidden={resolvedFields.has('quantity')}
        onAcceptChange={(val) => handleAcceptChange('quantity', val)}
        onHideIcons={() => handleResolveField('quantity')}
      />{documentSettings?.includeArea && <DocumentExtraCell position={position} field="area"/>}

      {/* Position */}
      <ActualsComparisonTableCell
        value={position?.position || comparisonData?.historyData?.position}
        field="position"
        decision={draftClientCellDecision(position, comparisonData, ['position'])}
        originalValue={comparisonData?.changes?.position?.base}
        onDecision={documentOnly ? decision => reviewField('position', decision) : undefined}
        requestedValue={comparisonData?.status !== 'deleted' ? comparisonData?.changes?.position?.requested : undefined}
        hasChanged={!!comparisonData?.changes?.position}
        isDeleted={isDeleted}
        cellSx={cellSx}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
        iconsHidden={resolvedFields.has('position')}
        onAcceptChange={(val) => handleAcceptChange('position', val)}
        onHideIcons={() => handleResolveField('position')}
      />{documentSettings?.includeToolsGear && <DocumentExtraCell position={position} field="toolsGear"/>}

      {/* Call Time */}
      <ActualsComparisonTableCell
        value={`${position?.startTime || comparisonData?.historyData?.startTime}-${position?.endTime || comparisonData?.historyData?.endTime}`}
        field="startTime"
        decision={draftClientCellDecision(position, comparisonData, ['startTime', 'endTime'])}
        originalValue={comparisonData?.changes?.startTime?.base}
        onDecision={documentOnly ? decision => reviewField('startTime', decision) : undefined}
        formatDisplayValue={value => String(value || '').split('-').map(clock => formatAttendanceDisplayTime(clock.trim(), '12')).join(' – ')}
        requestedValue={
          comparisonData?.status !== 'deleted' && (comparisonData?.changes?.startTime || comparisonData?.changes?.endTime)
            ? `${comparisonData?.changes?.startTime?.requested || position?.startTime || comparisonData?.historyData?.startTime}-${comparisonData?.changes?.endTime?.requested || position?.endTime || comparisonData?.historyData?.endTime}`
            : undefined
        }
        hasChanged={!!(comparisonData?.changes?.startTime || comparisonData?.changes?.endTime)}
        isDeleted={isDeleted}
        cellSx={cellSx}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
        iconsHidden={resolvedFields.has('startTime')}
        onAcceptChange={(val) => handleAcceptChange('startTime', val)}
        onHideIcons={() => handleResolveField('startTime')}
      />

      {/* Notes */}
      <ActualsComparisonTableCell
        value={position?.notes || ''}
        field="notes"
        decision={draftClientCellDecision(position, comparisonData, ['notes'])}
        originalValue={comparisonData?.changes?.notes?.base}
        onDecision={documentOnly ? decision => reviewField('notes', decision) : undefined}
        requestedValue={comparisonData?.status !== 'deleted' ? comparisonData?.changes?.notes?.requested : undefined}
        hasChanged={!!comparisonData?.changes?.notes}
        isDeleted={isDeleted}
        cellSx={cellSx}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
        iconsHidden={resolvedFields.has('notes')}
        onAcceptChange={(val) => handleAcceptChange('notes', val)}
        onHideIcons={() => handleResolveField('notes')}
      />

      {/* Rate Type */}
      <ActualsComparisonTableCell
        value={position?.rateType || ''}
        field="rateType"
        decision={draftClientCellDecision(position, comparisonData, ['rateType'])}
        originalValue={comparisonData?.changes?.rateType?.base}
        onDecision={documentOnly ? decision => reviewField('rateType', decision) : undefined}
        formatDisplayValue={value => rateTypeDisplayLabel(value, (value === position?.rateType ? position?.rateTypeLabel : '') || rateTypeOptions.find(option => option.value === value)?.label)}
        requestedValue={comparisonData?.status !== 'deleted' ? comparisonData?.changes?.rateType?.requested : undefined}
        hasChanged={!!comparisonData?.changes?.rateType}
        isDeleted={isDeleted}
        cellSx={{ ...cellSx, ...(unresolvedClientRate(position?.rateType, rateTypeOptions) ? { outline: '2px solid #ed6c02' } : {}) }}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
        iconsHidden={resolvedFields.has('rateType')}
        onAcceptChange={(val) => handleAcceptChange('rateType', val)}
        onHideIcons={() => handleResolveField('rateType')}
      />

      {/* Call Type */}
      <ActualsComparisonTableCell
        value={position?.callType || comparisonData?.historyData?.callType}
        field="callType"
        decision={draftClientCellDecision(position, comparisonData, ['callType'])}
        originalValue={comparisonData?.changes?.callType?.base}
        onDecision={documentOnly ? decision => reviewField('callType', decision) : undefined}
        requestedValue={comparisonData?.status !== 'deleted' ? comparisonData?.changes?.callType?.requested : undefined}
        hasChanged={!!comparisonData?.changes?.callType}
        isDeleted={isDeleted}
        cellSx={cellSx}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
        iconsHidden={resolvedFields.has('callType')}
        onAcceptChange={(val) => handleAcceptChange('callType', val)}
        onHideIcons={() => handleResolveField('callType')}
      />

      {/* Meal Break */}
      <ActualsComparisonTableCell
        value={position?.mealBreak || comparisonData?.historyData?.mealBreak}
        field="mealBreak"
        decision={draftClientCellDecision(position, comparisonData, ['mealBreak'])}
        originalValue={comparisonData?.changes?.mealBreak?.base}
        onDecision={documentOnly ? decision => reviewField('mealBreak', decision) : undefined}
        requestedValue={comparisonData?.status !== 'deleted' ? comparisonData?.changes?.mealBreak?.requested : undefined}
        hasChanged={!!comparisonData?.changes?.mealBreak}
        isDeleted={isDeleted}
        cellSx={cellSx}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
        iconsHidden={resolvedFields.has('mealBreak')}
        onAcceptChange={(val) => handleAcceptChange('mealBreak', val)}
        onHideIcons={() => handleResolveField('mealBreak')}
      />

      {/* Rate */}
      <ActualsComparisonTableCell
        value={position?.rate}
        field="rate"
        formatDisplayValue={value => value == null || value === '' ? <Tooltip title="Enter a reviewed rate for this position before including it in the document."><span>TBD</span></Tooltip> : `$${formatNumber(value)}`}
        decision={draftClientCellDecision(position, comparisonData, ['rate'])}
        originalValue={comparisonData?.changes?.rate?.base}
        onDecision={documentOnly ? decision => reviewField('rate', decision) : undefined}
        requestedValue={comparisonData?.status !== 'deleted' ? comparisonData?.changes?.rate?.requested : undefined}
        hasChanged={!!comparisonData?.changes?.rate}
        isDeleted={isDeleted}
        cellSx={cellSx}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
        iconsHidden={resolvedFields.has('rate')}
        onAcceptChange={(val) => handleAcceptChange('rate', val)}
        onHideIcons={() => handleResolveField('rate')}
      />

      {/* ST Hours */}
      <ActualsComparisonTableCell
        value={position?.registerHours ?? (comparisonData?.historyData?.registerHours || 0)}
        field="registerHours"
        decision={draftClientCellDecision(position, comparisonData, ['registerHours'])}
        originalValue={comparisonData?.changes?.registerHours?.base}
        onDecision={documentOnly ? decision => reviewField('registerHours', decision) : undefined}
        requestedValue={comparisonData?.status !== 'deleted' ? comparisonData?.changes?.registerHours?.requested : undefined}
        hasChanged={!!comparisonData?.changes?.registerHours}
        isDeleted={isDeleted}
        cellSx={cellSx}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
        iconsHidden={resolvedFields.has('registerHours')}
        onAcceptChange={(val) => handleAcceptChange('registerHours', val)}
        onHideIcons={() => handleResolveField('registerHours')}
      />

      {/* OT Hours */}
      <ActualsComparisonTableCell
        value={position?.overTimeHours ?? (comparisonData?.historyData?.overTimeHours || 0)}
        field="overTimeHours"
        decision={draftClientCellDecision(position, comparisonData, ['overTimeHours'])}
        originalValue={comparisonData?.changes?.overTimeHours?.base}
        onDecision={documentOnly ? decision => reviewField('overTimeHours', decision) : undefined}
        requestedValue={comparisonData?.status !== 'deleted' ? comparisonData?.changes?.overTimeHours?.requested : undefined}
        hasChanged={!!comparisonData?.changes?.overTimeHours}
        isDeleted={isDeleted}
        cellSx={cellSx}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
        iconsHidden={resolvedFields.has('overTimeHours')}
        onAcceptChange={(val) => handleAcceptChange('overTimeHours', val)}
        onHideIcons={() => handleResolveField('overTimeHours')}
      />

      {/* DT Hours */}
      <ActualsComparisonTableCell
        value={position?.DoubleTimeHours ?? (comparisonData?.historyData?.DoubleTimeHours || 0)}
        field="DoubleTimeHours"
        decision={draftClientCellDecision(position, comparisonData, ['DoubleTimeHours'])}
        originalValue={comparisonData?.changes?.DoubleTimeHours?.base}
        onDecision={documentOnly ? decision => reviewField('DoubleTimeHours', decision) : undefined}
        requestedValue={comparisonData?.status !== 'deleted' ? comparisonData?.changes?.DoubleTimeHours?.requested : undefined}
        hasChanged={!!comparisonData?.changes?.DoubleTimeHours}
        isDeleted={isDeleted}
        cellSx={cellSx}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
        iconsHidden={resolvedFields.has('DoubleTimeHours')}
        onAcceptChange={(val) => handleAcceptChange('DoubleTimeHours', val)}
        onHideIcons={() => handleResolveField('DoubleTimeHours')}
      />

      {(documentSettings?.includePerDiem ?? includePerDiem) && (
        <ActualsComparisonTableCell
          value={position?.addOns || comparisonData?.historyData?.addOns || 0}
          field="addOns"
        decision={draftClientCellDecision(position, comparisonData, ['addOns'])}
        originalValue={comparisonData?.changes?.addOns?.base}
        onDecision={documentOnly ? decision => reviewField('addOns', decision) : undefined}
          requestedValue={comparisonData?.status !== 'deleted' ? comparisonData?.changes?.addOns?.requested : undefined}
          hasChanged={!!comparisonData?.changes?.addOns}
          isDeleted={isDeleted}
          cellSx={cellSx}
          onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
          iconsHidden={resolvedFields.has('addOns')}
          onAcceptChange={(val) => handleAcceptChange('addOns', val)}
          onHideIcons={() => handleResolveField('addOns')}
        />
      )}

      {(documentSettings?.includeMiscFees ?? includeMiscFees) && (
        <ActualsComparisonTableCell
          value={position?.miscFees || comparisonData?.historyData?.miscFees || 0}
          field="miscFees"
        decision={draftClientCellDecision(position, comparisonData, ['miscFees'])}
        originalValue={comparisonData?.changes?.miscFees?.base}
        onDecision={documentOnly ? decision => reviewField('miscFees', decision) : undefined}
          requestedValue={comparisonData?.status !== 'deleted' ? comparisonData?.changes?.miscFees?.requested : undefined}
          hasChanged={!!comparisonData?.changes?.miscFees}
          isDeleted={isDeleted}
          cellSx={cellSx}
          onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
          iconsHidden={resolvedFields.has('miscFees')}
          onAcceptChange={(val) => handleAcceptChange('miscFees', val)}
          onHideIcons={() => handleResolveField('miscFees')}
        />
      )}

      {/* Total */}
      <ActualsComparisonTableCell
        value={position?.totals}
        field="totals"
        formatDisplayValue={value => position.clientRatePending ? <Tooltip title="Enter a reviewed rate in this row to calculate its document total."><span>TBD</span></Tooltip> : `$${formatNumber(value)}`}
        decision={draftClientCellDecision(position, comparisonData, ['totals'])}
        originalValue={comparisonData?.changes?.totals?.base}
        onDecision={documentOnly ? decision => reviewField('totals', decision) : undefined}
        hasChanged={false}
        isDeleted={isDeleted}
        cellSx={cellSx}
        onClick={position.quoteReferenceLabel ? undefined : onStartEdit}
      />
    </TableRow>
  );
};

export default ActualsInlineShiftRow;
